import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useIntl } from 'react-intl-next';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid';

import { ACTION } from '@atlaskit/editor-common/analytics';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	isForgeAgentByCreatorType,
	type AgentCreatorType,
} from '@atlaskit/rovo-agent-components/common/utils/is-forge-agent';
import { AgentAvatar } from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import type {
	ConversationStarter,
	StaticAgentConversationStarter,
} from '@atlaskit/rovo-agent-components/ui/AgentConversationStarters';
import { type ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { useContextualConversationStarters } from '@atlassian/conversation-assistant/controllers/contextual-conversation-starters';
import { createUnifiedAnalyticsPayload } from '@atlassian/editor-ai-common/analytics/create-unified-analytics-payload';
import { mapToAssistanceServiceProduct } from '@atlassian/editor-ai-common/api/assistance-service';
import type { Suggestion } from '@atlassian/generative-ai-modal/screens/UserInputCommandPalette';
import { type EditorAgent } from '@atlassian/generative-ai-modal/utils/agents';

import { type EditorPluginAIProvider } from '../../types/types';
import { type HandleHighlightCbType } from '../components/ExperienceApplication/screens-with-logic/UserInputCommandPaletteWithLogic';
import type { EditorPluginAIConfigItem } from '../prebuilt/config-items/config-items';
import { useAnalyticsFlow } from '../utils/analytics/analyticsFlowUtils';
import { useFireAIAnalyticsEvent } from '../utils/analytics/utils';
import { getDummySuggestions } from '../utils/dummy-suggestions';

import { useAIExperienceCommonDataContext } from './useAIExperienceCommonData';

const experienceName = 'Rovo agent conversation starters';
const suggestionCount = 3;

export function useAgentStarters({
	handleHighlight,
	configItem,
	onConversationStarterSelected,
	product,
	editorPluginAIProvider,
}: {
	configItem: EditorPluginAIConfigItem;
	editorPluginAIProvider: EditorPluginAIProvider;
	handleHighlight: HandleHighlightCbType;
	onConversationStarterSelected: (conversationStarter: string) => void;
	product: string;
}) {
	let agent: EditorAgent | undefined;
	/**
	 * When agent is silently used behind scenes for prompt then don't show agent starter.
	 * That's why don't assign agent from configItem to agent variable.
	 */
	if (!configItem.silentAgentPrompt) {
		agent = configItem.agent;
	}

	const { formatMessage } = useIntl();

	const siteId = useMemo(
		() => editorPluginAIProvider?.getChannelVariables?.('rovo-agent')?.cloudId || '',
		[editorPluginAIProvider],
	);

	const [hasError, setHasError] = useState(false);
	const fireAIAnalyticsEvent = useFireAIAnalyticsEvent();
	const analyticsFlow = useAnalyticsFlow();
	const [lastAiInteractionID, setLastAiInteractionID] = useState('');

	const getAiLifeCycleDynamicAttributes = analyticsFlow.getAiLifeCycleDynamicAttributes;

	const userDefinedAgentStarters = useMemo(() => {
		if (!agent) {
			return [];
		}

		return agent.conversationStarters.filter((conversationStarter) => {
			return conversationStarter.type === 'user-defined';
		});
	}, [agent]);

	const aiExperienceCommonData = useAIExperienceCommonDataContext();

	const onInit = useCallback(() => {
		setHasError(false);

		// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
		const aiId = uuid();
		setLastAiInteractionID(aiId);

		fireAIAnalyticsEvent({
			payload: createUnifiedAnalyticsPayload({
				action: ACTION.INITIATED,
				interactionID: aiId,
				experienceName,
				isProactive: false,
				invokedFrom: aiExperienceCommonData.lastTriggeredFrom,
				attributes: getAiLifeCycleDynamicAttributes?.({ init: true }),
			}),
		});
	}, [
		aiExperienceCommonData.lastTriggeredFrom,
		fireAIAnalyticsEvent,
		getAiLifeCycleDynamicAttributes,
	]);

	const onError = useCallback(
		(error: ApiError) => {
			setHasError(true);
			fireAIAnalyticsEvent({
				payload: createUnifiedAnalyticsPayload({
					action: ACTION.ERROR,
					interactionID: lastAiInteractionID,
					experienceName,
					isProactive: false,
					invokedFrom: aiExperienceCommonData.lastTriggeredFrom,
					attributes: {
						aiErrorMessage: error.message,
						aiErrorCode: 500,
						...getAiLifeCycleDynamicAttributes?.({ init: false }),
					},
				}),
			});
		},
		[
			aiExperienceCommonData.lastTriggeredFrom,
			fireAIAnalyticsEvent,
			lastAiInteractionID,
			getAiLifeCycleDynamicAttributes,
		],
	);

	const {
		getDynamicContextualStarters,
		starters: contextualStarters,
		isMutating,
	} = useContextualConversationStarters({
		onInit,
		onError,
		assistanceServiceParams: {
			touchpointSource: 'editor-ai-conversation-starters',
			product: mapToAssistanceServiceProduct(product),
			experienceId: 'ai-mate',
			siteId,
		},
	});

	const isPending = useMemo(() => {
		return (
			isMutating ||
			(contextualStarters.some((cs) => !cs) && userDefinedAgentStarters.length < suggestionCount)
		);
	}, [isMutating, contextualStarters, userDefinedAgentStarters]);

	useEffect(() => {
		if (agent && userDefinedAgentStarters.length < suggestionCount) {
			getDynamicContextualStarters({
				locationUrl: window.location.href,
				fallbackStarters: agent.conversationStarters.map((conversationStarter) => {
					if (typeof conversationStarter.message !== 'string') {
						conversationStarter.message = formatMessage(conversationStarter.message);
					}

					return conversationStarter as ConversationStarter;
				}),
			});
		}
	}, [agent, getDynamicContextualStarters, formatMessage, userDefinedAgentStarters]);

	const starters = useMemo<Suggestion[]>(() => {
		if (!agent) {
			return [];
		}

		let conversationStarters: (ConversationStarter | StaticAgentConversationStarter)[] =
			userDefinedAgentStarters;

		if (isPending) {
			return getDummySuggestions(agent, suggestionCount);
		}

		if (conversationStarters.length < suggestionCount) {
			// The linter was being very agressive about this typing for some reason
			// so I had to comfort it and make sure it was aware this variable definitely did
			// not contain nulls.
			// See: How to win friends and influence people by Dale Carnegie
			const nonNullContextualStarters = contextualStarters.filter(
				(conversationStarter) => conversationStarter !== null,
			) as ConversationStarter[];
			const availableContextualStarters: ConversationStarter[] = nonNullContextualStarters.slice(
				0,
				suggestionCount - conversationStarters.length,
			);

			conversationStarters = [...conversationStarters, ...availableContextualStarters];
		}

		return conversationStarters.map(({ message: conversationStarter }, index) => {
			const translatedMessage =
				typeof conversationStarter === 'string'
					? conversationStarter
					: formatMessage(conversationStarter);

			return {
				value: `agent:${agent.id}:${index}:${translatedMessage}`,
				label: translatedMessage,
				icon: (
					<AgentAvatar
						agentId={agent.id}
						agentNamedId={agent.externalConfigReference ?? agent.namedId}
						agentIdentityAccountId={agent.identityAccountId}
						isForgeAgent={
							fg('rovo_agent_support_a2a_avatar')
								? isForgeAgentByCreatorType(agent.creatorType as AgentCreatorType)
								: agent.creatorType === 'FORGE'
						}
						forgeAgentIconUrl={agent.icon}
					/>
				),
				groupHeading: '',
				onSelect: () => {
					onConversationStarterSelected(translatedMessage);
				},
				onSuggestionFocused: () => {
					handleHighlight({
						configItem,
					});
				},
				...(fg('ai-smart-button-team-25') && {
					isAgent: true,
				}),
			};
		});
	}, [
		agent,
		isPending,
		userDefinedAgentStarters,
		contextualStarters,
		formatMessage,
		onConversationStarterSelected,
		handleHighlight,
		configItem,
	]);

	useEffect(() => {
		if (!isPending && !hasError && starters.length) {
			fireAIAnalyticsEvent({
				payload: createUnifiedAnalyticsPayload({
					action: ACTION.VIEWED,
					interactionID: lastAiInteractionID,
					experienceName,
					isProactive: false,
					invokedFrom: aiExperienceCommonData.lastTriggeredFrom,
					attributes: getAiLifeCycleDynamicAttributes?.({ init: true }),
				}),
			});
		}
	}, [
		aiExperienceCommonData.lastTriggeredFrom,
		isPending,
		hasError,
		starters,
		fireAIAnalyticsEvent,
		lastAiInteractionID,
		getAiLifeCycleDynamicAttributes,
	]);

	return {
		starters,
	};
}
